// reauth.js — Sudo-mode reauth helper.
//
// GitHub-style sudo mode: an in-memory short-lived token that the
// coordinator mints in exchange for the user's password. The token
// unlocks a 5-minute window for sensitive operations (password
// change, delete account, 2FA disable). Never persisted — always
// lives only in module-local state.
//
// Bound to a fixed reauth modal DOM (#reauth-backdrop + #reauth-
// password + #reauth-confirm + #reauth-cancel + #reauth-error)
// declared in index.html. showReauthPrompt() wires + unwires its
// listeners per-call so consumers don't have to worry about cleanup.
//
// API:
//   requireReauth() → Promise<string>   — token; prompts if cache empty.
//                                         throws Error('reauth_cancelled')
//                                         on user cancel.
//   clearReauth()                       — drop the cached token. Callers
//                                         use this after a 403/reauth_required
//                                         response so the next call re-prompts.
//
// Bridge dependencies: getSupabase() lazily. COORDINATOR read from
// window.AC_CONFIG.API_BASE.
//
// Both are re-exposed on AC_LEGACY by bootstrap (existing consumers:
// account-email-password.js, account-actions.js, account-mfa.js).
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2l).
import { $ } from "./dom.js";
import { showToast } from "./ui.js";

const COORDINATOR =
  (typeof window !== "undefined" &&
    window.AC_CONFIG &&
    window.AC_CONFIG.API_BASE) ||
  "";

function getSupabase() {
  const legacy = window.AC_LEGACY;
  return legacy && typeof legacy.getSupabase === "function"
    ? legacy.getSupabase()
    : null;
}

let _reauthToken = null;
let _reauthExpiry = 0; // epoch ms

export function clearReauth() {
  _reauthToken = null;
  _reauthExpiry = 0;
}

function showReauthPrompt() {
  return new Promise((resolve) => {
    const bd = $("reauth-backdrop");
    const input = $("reauth-password");
    const err = $("reauth-error");
    const ok = $("reauth-confirm");
    const cancel = $("reauth-cancel");
    input.value = "";
    err.textContent = "";
    ok.disabled = false;
    ok.textContent = "Confirm";
    bd.classList.add("open");
    setTimeout(() => {
      try {
        input.focus();
      } catch (_) {}
    }, 50);

    const cleanup = () => {
      bd.classList.remove("open");
      ok.removeEventListener("click", onOK);
      cancel.removeEventListener("click", onCancel);
      bd.removeEventListener("click", onBackdrop);
      input.removeEventListener("keydown", onKey);
    };
    const done = (val) => {
      cleanup();
      resolve(val);
    };
    const onOK = () => {
      const pw = input.value;
      if (!pw) {
        err.textContent = "Enter your password.";
        return;
      }
      done(pw);
    };
    const onCancel = () => done(null);
    const onBackdrop = (e) => {
      if (e.target === bd) done(null);
    };
    const onKey = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onOK();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    };
    ok.addEventListener("click", onOK);
    cancel.addEventListener("click", onCancel);
    bd.addEventListener("click", onBackdrop);
    input.addEventListener("keydown", onKey);
  });
}

// requireReauth returns a valid reauth token, prompting for the
// password if the cache is empty or within 5s of expiry. Throws
// 'reauth_cancelled' on user cancel and a network/other Error on
// incorrect password. Callers should catch and bail out silently on
// 'reauth_cancelled' (the user already saw the modal).
export async function requireReauth() {
  if (_reauthToken && _reauthExpiry > Date.now() + 5000) return _reauthToken;
  // Loop so a wrong password lets them retry without a second modal.
  // Cancel breaks out with 'reauth_cancelled'.
  while (true) {
    const pw = await showReauthPrompt();
    if (pw === null) throw new Error("reauth_cancelled");
    const supabase = getSupabase();
    if (!supabase) throw new Error("Auth not ready.");
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error("Not signed in.");
    // Re-open the modal momentarily so we can show an inline error on
    // bad pw. Easier: just call /reauthenticate and if it fails,
    // re-show prompt.
    let res;
    try {
      res = await fetch(COORDINATOR + "/v1/user/reauthenticate", {
        method: "POST",
        headers: {
          Authorization: "Bearer " + session.access_token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password: pw }),
      });
    } catch (e) {
      showToast("error", "Network error confirming password. Try again.");
      throw new Error("network");
    }
    if (!res.ok) {
      let msg = "Incorrect password.";
      try {
        const j = await res.json();
        msg = j.message || j.error || msg;
      } catch {}
      // Surface the error via a toast and re-prompt. Good enough UX
      // without reworking the modal to persist and accept an inline
      // error.
      showToast("error", msg);
      continue;
    }
    const body = await res.json().catch(() => ({}));
    if (!body.token || !body.expires_at) {
      showToast("error", "Unexpected reauth response.");
      throw new Error("bad_reauth_response");
    }
    _reauthToken = body.token;
    _reauthExpiry = new Date(body.expires_at).getTime();
    return _reauthToken;
  }
}
